import { useEffect, useRef, useState } from "react"
import { ChevronUp, ChevronDown } from "lucide-react"
import type { CalendarEvent } from "../../types/agenda"
import { EVENT_COLORS, EVENT_COLORS_LIGHT, EVENT_TEXT_COLORS, EVENT_TIME_COLORS, HOUR_SLOT_HEIGHT, DEFAULT_SCROLL_HOUR } from "../../constants/agenda"
import { formatTime, parseWorkingHoursRange } from "../../utils/agenda"
import { CalendarAllDayEvents } from "./CalendarAllDayEvents"

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const DAYS = ["lun", "mar", "mer", "gio", "ven", "sab", "dom"]

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(":").map(Number)
  return hours * 60 + minutes
}

interface CalendarWeekViewProps {
  currentDate: Date
  events: CalendarEvent[]
  onEventClick?: (event: CalendarEvent) => void
  onEmptyCellClick?: (date: Date) => void
  workingHours?: string
  highlightedEventId?: number | null
}

export function CalendarWeekView({ currentDate, events, onEventClick, onEmptyCellClick, workingHours, highlightedEventId }: CalendarWeekViewProps) {
  // Parse working hours from user settings
  const { startHour: workStartHour, endHour: workEndHour } = parseWorkingHoursRange(workingHours)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(true)

  // Calculate week dates based on currentDate
  const getWeekDates = () => {
    const weekStart = new Date(currentDate)
    const dayOfWeek = weekStart.getDay()
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek // Monday is 1
    weekStart.setDate(weekStart.getDate() + diff)

    const dates = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart)
      date.setDate(weekStart.getDate() + i)
      dates.push(date)
    }
    return dates
  }

  const weekDates = getWeekDates()

  // Scroll to working hours start (or default) on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      const scrollTo = (workStartHour > 0 ? workStartHour - 1 : DEFAULT_SCROLL_HOUR) * HOUR_SLOT_HEIGHT
      scrollContainerRef.current.scrollTop = scrollTo
      updateScrollState()
    }
  }, [])

  const updateScrollState = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
      setCanScrollUp(scrollTop > 0)
      setCanScrollDown(scrollTop < scrollHeight - clientHeight - 1)
    }
  }

  const scrollByHours = (hours: number) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        top: hours * HOUR_SLOT_HEIGHT,
        behavior: "smooth",
      })
    }
  }

  const getEventPosition = (event: CalendarEvent) => {
    const startMinutes = timeToMinutes(event.start_time)
    const endMinutes = event.end_time ? timeToMinutes(event.end_time) : startMinutes + 60
    const durationMinutes = endMinutes - startMinutes

    const top = (startMinutes / 60) * 60
    const height = (durationMinutes / 60) * 60

    return { top, height }
  }

  const getTodayColumn = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    for (let i = 0; i < weekDates.length; i++) {
      const weekDate = new Date(weekDates[i])
      weekDate.setHours(0, 0, 0, 0)
      if (weekDate.getTime() === today.getTime()) {
        return i + 1
      }
    }
    return null
  }

  const getEventsForDay = (dayIndex: number) => {
    const date = weekDates[dayIndex]
    return events.filter((event) => {
      const eventDate = new Date(event.start_date)
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      )
    })
  }

  const todayColumn = getTodayColumn()

  // Scroll-into-view sull'evento evidenziato (es. dopo click su notifica)
  useEffect(() => {
    if (!highlightedEventId) return
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-event-id="${highlightedEventId}"]`)
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 300)
    return () => clearTimeout(timer)
  }, [highlightedEventId])

  // Current time line position (updates every minute)
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(interval)
  }, [])

  const currentTimeTop = todayColumn !== null
    ? (now.getHours() * 60 + now.getMinutes()) / 60 * HOUR_SLOT_HEIGHT
    : 0

  return (
    <div className="bg-[var(--hexa-card-bg)] rounded-lg overflow-hidden relative hexa-section-shadow border border-[rgba(106,77,255,0.25)]">
      {/* Scroll arrows - positioned absolutely over the scrollable container */}
      <div className="flex items-center justify-end gap-2 border-b border-[var(--hexa-border)] px-3 py-1">
        <button
          onClick={() => scrollByHours(-2)}
          disabled={!canScrollUp}
          className="text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Scroll up"
        >
          <ChevronUp className="h-2.5 w-2.5" />
        </button>
        <button
          onClick={() => scrollByHours(2)}
          disabled={!canScrollDown}
          className="text-[var(--hexa-text-muted)] hover:text-[var(--hexa-text-secondary)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Scroll down"
        >
          <ChevronDown className="h-2.5 w-2.5" />
        </button>
      </div>

      <CalendarAllDayEvents events={weekDates.flatMap((_, index) => getEventsForDay(index))} onEventClick={onEventClick} showDate />
      {/* Single scrollable container for both header and grid */}
      <div
        ref={scrollContainerRef}
        onScroll={updateScrollState}
        aria-label="Calendario settimanale"
        tabIndex={0}
        className="agenda-timeline overflow-y-auto overflow-x-hidden scrollbar-hexa"
      >
        {/* Sticky header with days */}
        <div className="sticky top-0 z-10 flex min-w-0 border-b border-[rgba(106,77,255,0.25)] bg-[var(--hexa-card-bg)]">
          {/* Time column placeholder for alignment */}
          <div className="w-8 sm:w-16 shrink-0 border-r border-[rgba(106,77,255,0.25)]" />

          {/* Day headers */}
          {DAYS.map((day, index) => {
            const isToday = todayColumn === index + 1
            return (
              <div
                key={day}
                className={`min-w-0 flex-1 text-center py-3 border-r border-[rgba(106,77,255,0.25)] last:border-r-0 ${
                  isToday ? "bg-[var(--hexa-purple)]/10 border-b-2 border-b-[var(--hexa-purple)]" : ""
                }`}
              >
                <div className="text-[var(--hexa-text-secondary)] text-[10px] uppercase font-medium mb-0.5">{day}</div>
                <div
                  className={`text-[var(--hexa-text-primary)] font-semibold text-base ${
                    isToday ? "bg-[var(--hexa-purple)] rounded-full w-6 h-6 flex items-center justify-center mx-auto text-xs text-white" : ""
                  }`}
                  style={isToday ? { boxShadow: "0 0 0 3px rgba(123, 63, 255, 0.25)" } : undefined}
                >
                  {weekDates[index].getDate()}
                </div>
              </div>
            )
          })}
        </div>

        {/* Scrollable grid area */}
        <div className="relative flex min-w-0">
          {/* Linee orizzontali per fascia orario lavorativo */}
          <div
            className="absolute left-8 sm:left-16 right-0 h-[2px] pointer-events-none z-10"
            style={{
              top: `${workStartHour * 45}px`,
              backgroundColor: "var(--working-hours-line)",
            }}
          />
          <div
            className="absolute left-8 sm:left-16 right-0 h-[2px] pointer-events-none z-10"
            style={{
              top: `${workEndHour * 45}px`,
              backgroundColor: "var(--working-hours-line)",
            }}
          />

          {/* Time column */}
          <div className="w-8 sm:w-16 shrink-0 border-r border-[rgba(106,77,255,0.25)]">
            {HOURS.map((hour) => {
              const isWorkingHour = hour >= workStartHour && hour < workEndHour
              return (
                <div
                  key={hour}
                  className="h-[45px] border-b border-[rgba(106,77,255,0.25)] pr-0.5 sm:pr-4 pt-1 text-right flex items-start justify-end"
                  style={isWorkingHour ? {
                    borderLeft: "2px solid var(--working-hours-border)",
                  } : undefined}
                >
                  <span className={`text-[8px] sm:text-[10px] ${isWorkingHour ? "text-[var(--hexa-text-secondary)]" : "text-[var(--hexa-text-muted)]"}`}>
                    {hour.toString().padStart(2, "0")}:00
                  </span>
                </div>
              )
            })}
          </div>

          {/* Days grid with events */}
          {DAYS.map((day, dayIndex) => {
            const dayEvents = getEventsForDay(dayIndex)
            const isToday = todayColumn === dayIndex + 1
            const isFirstColumn = dayIndex === 0

            return (
              <div
                key={day}
                className={`min-w-0 flex-1 relative border-r border-[rgba(106,77,255,0.25)] last:border-r-0 ${
                  isToday ? "bg-[var(--hexa-purple)]/5" : ""
                }`}
              >
                {/* Hour lines */}
                {HOURS.map((hour) => {
                  const isWorkingHour = hour >= workStartHour && hour < workEndHour
                  return (
                    <div
                      key={hour}
                      className="h-[45px] border-b border-[rgba(106,77,255,0.25)] cursor-pointer hover:bg-[rgba(106,77,255,0.08)] transition-colors"
                      style={isWorkingHour ? {
                        background: "var(--working-hours-bg)",
                        borderLeft: isFirstColumn ? "2px solid var(--working-hours-border)" : undefined,
                      } : undefined}
                      onClick={() => {
                        const clickedDate = new Date(weekDates[dayIndex])
                        clickedDate.setHours(hour, 0, 0, 0)
                        onEmptyCellClick?.(clickedDate)
                      }}
                    />
                  )
                })}

                {/* Events positioned absolutely */}
                {dayEvents.map((event) => {
                  // Date-only events have no position in the hourly grid (all-day UI: S06).
                  if (!event.start_time) return null
                  const { top, height } = getEventPosition(event)
                  const type = event.type || 'default'
                  const bgColor = EVENT_COLORS_LIGHT[type] || EVENT_COLORS_LIGHT.default
                  const borderColor = EVENT_COLORS[type] || EVENT_COLORS.default
                  const textColor = EVENT_TEXT_COLORS[type] || EVENT_TEXT_COLORS.default
                  const timeColor = EVENT_TIME_COLORS[type] || EVENT_TIME_COLORS.default
                  return (
                    <button
                      key={event.id}
                      data-event-id={event.id}
                      aria-label={`${formatTime(event.start_time)} ${event.title}`}
                      title={`${formatTime(event.start_time)} ${event.title}`}
                      className={`absolute left-0.5 right-0.5 sm:left-1 sm:right-1 flex flex-col rounded-md px-0.5 sm:px-1.5 py-1 text-left cursor-pointer hover:opacity-90 transition-opacity overflow-hidden ${highlightedEventId === event.id ? "task-highlight" : ""}`}
                      style={{
                        top: `${top * 0.75}px`,
                        height: `${height * 0.75}px`,
                        backgroundColor: bgColor,
                        borderLeft: `3px solid ${borderColor}`,
                      }}
                      onClick={(e) => { e.stopPropagation(); onEventClick?.(event) }}
                    >
                      <div className="order-1 sm:order-none w-full min-w-0 line-clamp-2 font-medium text-[8px] sm:text-[10px] leading-tight break-words" style={{ color: textColor }}>
                        {event.title}
                      </div>
                      <div className="order-0 sm:order-none w-full min-w-0 truncate text-[8px] sm:text-[9px] mt-0.5" style={{ color: timeColor }}>
                        {formatTime(event.start_time)}
                        {event.end_time && ` - ${formatTime(event.end_time)}`}
                      </div>
                      {event.location && (
                        <div className="order-2 sm:order-none w-full min-w-0 text-[8px] sm:text-[9px] mt-0.5 truncate" style={{ color: timeColor }}>
                          {event.location}
                        </div>
                      )}
                    </button>
                  )
                })}

                {/* Current time line — only on today's column */}
                {isToday && (
                  <div
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: `${currentTimeTop}px` }}
                  >
                    <div
                      className="absolute rounded-full"
                      style={{
                        width: 8,
                        height: 8,
                        background: "var(--hexa-purple)",
                        left: -4,
                        top: -3,
                      }}
                    />
                    <div
                      className="w-full"
                      style={{
                        height: 2,
                        background: "var(--hexa-purple)",
                      }}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
